import { BitDepth, SensorRawDataFormat, SensorType, SproutSensorID } from "../shared.js";
import { SensorConfigurations } from "./SensorConfigurations/SensorConfiguration.js";

export type SproutSensorIDSpec = {
    bitDepth: BitDepth;
    hardwareType: SensorRawDataFormat;
    minSupportedSproutVersion: number;
    minValue?: number;
    maxValue?: number;
};

export const sproutSensorIDTable: Record<SproutSensorID, SproutSensorIDSpec> = {
    0: { bitDepth: 8, hardwareType: "ADC", minSupportedSproutVersion: 0, minValue: 0, maxValue: 5 },
    1: { bitDepth: 12, hardwareType: "ADC", minSupportedSproutVersion: 0, minValue: 0, maxValue: 5 },
    2: { bitDepth: 16, hardwareType: "ADC", minSupportedSproutVersion: 99, minValue: 0, maxValue: 5 },
    3: { bitDepth: 32, hardwareType: "ADC", minSupportedSproutVersion: 99, minValue: 0, maxValue: 5 },

    4: { bitDepth: 8, hardwareType: "PWM", minSupportedSproutVersion: 0, minValue: 0, maxValue: 1000 },
    5: { bitDepth: 12, hardwareType: "PWM", minSupportedSproutVersion: 0, minValue: 0, maxValue: 1000 },
    6: { bitDepth: 16, hardwareType: "PWM", minSupportedSproutVersion: 0, minValue: 0, maxValue: 1000 },
    7: { bitDepth: 32, hardwareType: "PWM", minSupportedSproutVersion: 99, minValue: 0, maxValue: 1000 },

    8: { bitDepth: 16, hardwareType: "TEMPHUM", minSupportedSproutVersion: 0 },
    9: { bitDepth: 24, hardwareType: "TEMPHUM", minSupportedSproutVersion: 99 },
    10: { bitDepth: 32, hardwareType: "TEMPHUM", minSupportedSproutVersion: 0 },
    11: { bitDepth: 64, hardwareType: "TEMPHUM", minSupportedSproutVersion: 99 },

    12: { bitDepth: 16, hardwareType: "ACCEL", minSupportedSproutVersion: 99 },
    13: { bitDepth: 24, hardwareType: "ACCEL", minSupportedSproutVersion: 99 },
    14: { bitDepth: 32, hardwareType: "ACCEL", minSupportedSproutVersion: 99 },
    15: { bitDepth: 64, hardwareType: "ACCEL", minSupportedSproutVersion: 99 },
};

//gets populated automatically from the chart above.
export const SproutSensorIDsByBitDepthByHardwareType: Record<SensorRawDataFormat, Record<BitDepth, SproutSensorID>> = {
    ACCEL: {},
    ADC: {},
    PWM: {},
    TEMPHUM: {},
    PULSE_TOTAL: {},
};
Object.keys(SproutSensorIDsByBitDepthByHardwareType).forEach((htype) => {
    const relevantIDs = Object.keys(sproutSensorIDTable).filter((id) => sproutSensorIDTable[id].hardwareType === htype);
    relevantIDs.forEach((relID) => {
        SproutSensorIDsByBitDepthByHardwareType[htype][sproutSensorIDTable[relID].bitDepth] = parseInt(relID);
    });
});

/**
 * A single Sprout sensor reading, decoded from the firmware's encoded integer into the underlying
 * physical effect.
 *
 * Terminology matches the rest of the time series pipeline:
 * - **encoded** is the raw integer the firmware transmits (unprocessed bits),
 * - **decoded** is the underlying physical effect — volts for an `ADC` sensor, hertz for a `PWM`
 *   one — which is what a data stream stores in `decoded_value`,
 * - **converted** is the engineering value produced later by the sensor element's conversion.
 */
export type DecodedSproutReading = {
    /**
     * The underlying physical effect, in the units implied by `hardwareType`: volts for `ADC`,
     * hertz for `PWM`. `null` when `encodedValue` is the firmware's no-data sentinel.
     *
     * For `TEMPHUM` and `ACCEL` there is no min/max in {@link sproutSensorIDTable}, so this is only
     * bit-depth-normalized (0..1) and is not meaningful on its own — those hardware types pack
     * several fields into one reading and must be split instead. See {@link decodeSproutTempHum}.
     */
    decodedValue: number | null;
    hardwareType: SensorRawDataFormat;
    /** The bit depth actually used, after any `bitDepthOverride`. */
    bitDepth: number;
};

/**
 * Decodes one Sprout sensor reading from its encoded firmware integer.
 *
 * Normalizes against the bit depth, then maps onto the hardware type's physical range from
 * {@link sproutSensorIDTable} (0-5 V for `ADC`, 0-1000 Hz for `PWM`). An all-ones encoded value is
 * the firmware's no-data sentinel and yields a `null` decoded value rather than a bogus maximum.
 *
 * This is the single source of truth for that arithmetic: both the legacy MongoDB path (via
 * `SproutSensorHelper.getDataPointFromReading`) and the data-stream path call it, so the two cannot
 * drift.
 *
 * @param bitDepthOverride Verification uplinks pack every reading at a fixed 16 bits regardless of
 * the sensor ID, so they must pass the depth they actually used rather than trusting the table.
 */
export function decodeSproutSensorReading({
    encodedValue,
    sensorID,
    bitDepthOverride,
}: {
    encodedValue: number;
    sensorID: SproutSensorID;
    bitDepthOverride?: number;
}): DecodedSproutReading {
    const spec = sproutSensorIDTable[sensorID];
    if (!spec) {
        throw new Error(`Cannot decode Sprout reading: ${sensorID} is not a known Sprout sensor ID`);
    }

    const bitDepth = bitDepthOverride ?? spec.bitDepth;

    // The firmware reserves the all-ones value to mean "no reading available".
    if (encodedValue === 2 ** bitDepth - 1) {
        return { decodedValue: null, hardwareType: spec.hardwareType, bitDepth };
    }

    // Note the divisor is 2**bitDepth - 2, not - 1: the sentinel is excluded from the usable range.
    const bitDepthNormalizedValue = encodedValue / (2 ** bitDepth - 2);

    // Hardware types without a declared range (TEMPHUM, ACCEL) stay bit-depth-normalized. This lets
    // an ID carry more range with less precision, or the reverse, without changing this code.
    if (spec.minValue === undefined || spec.maxValue === undefined) {
        return { decodedValue: bitDepthNormalizedValue, hardwareType: spec.hardwareType, bitDepth };
    }

    return {
        decodedValue: bitDepthNormalizedValue * (spec.maxValue - spec.minValue) + spec.minValue,
        hardwareType: spec.hardwareType,
        bitDepth,
    };
}

/**
 * Splits a packed Sprout `TEMPHUM` reading into its two measurements.
 *
 * The firmware packs two half-width fields into one encoded value: the high half is temperature, the
 * low half humidity. Each half has its own all-ones no-data sentinel, so one can be present while
 * the other is absent.
 *
 * The returned values are already in the units the onboard temp/hum sensor elements declare as their
 * `decodedUnits` (celsius and percent relative humidity), which is why those elements use the
 * `unity` conversion — there is nothing left to convert.
 *
 * Single source of truth for both the legacy `v1temphum` conversion and the data-stream path.
 *
 * Note the half-extraction uses a JavaScript bitshift, which operates on 32 bits. That is correct
 * for sensor IDs 8 and 10 (bit depths 16 and 32) but would silently no-op for ID 11 (bit depth 64,
 * shift of 32). ID 11 has `minSupportedSproutVersion: 99`, i.e. no firmware supports it, so this is
 * latent rather than live. Behaviour is preserved from the original conversion deliberately.
 */
export function decodeSproutTempHum({ encodedValue, bitDepth }: { encodedValue: number; bitDepth: number }): {
    temperatureCelsius: number | null;
    humidityPercentRh: number | null;
} {
    const halfWidth = bitDepth / 2;
    const fullReading = 2 ** halfWidth - 1;

    const temperatureReading = (encodedValue >> halfWidth) % (fullReading + 1);
    const humidityReading = encodedValue % (fullReading + 1);

    return {
        temperatureCelsius: temperatureReading === fullReading ? null : 175 * (temperatureReading / fullReading) - 45,
        humidityPercentRh: humidityReading === fullReading ? null : (humidityReading / fullReading) * 125 - 6,
    };
}

/**
 * Gets the sprout ID that should be used for a given sensor type. If a sensor type is not valid, then it
 * returns null
 * @param sensorType
 * @returns {any}
 */
export function getIdealSproutSensorIDForSensorType(sensorType: SensorType): number | null {
    const config = SensorConfigurations[sensorType];
    if (!config?.verdiDeviceSensorConfiguration) {
        return null;
    }
    const idealBitDepthsForPrecisionImportances = {
        low: 8,
        medium: 12,
        high: 16,
    };
    let idealBitDepth =
        idealBitDepthsForPrecisionImportances[config.verdiDeviceSensorConfiguration.precisionImportance] || 12;

    // these sensors normally have twice the bit depth
    if (["TEMPHUM", "ACCEL"].includes(config.verdiDeviceSensorConfiguration.rawDataFormat)) {
        idealBitDepth = idealBitDepth * 2;
    }
    const IDsByBitDepth = SproutSensorIDsByBitDepthByHardwareType[config.verdiDeviceSensorConfiguration.rawDataFormat];
    return IDsByBitDepth[idealBitDepth] || IDsByBitDepth[Object.keys(IDsByBitDepth)[1]];
}
